import React, { createContext, useContext, useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useUploader, makeId } from "@utils";
import { toast } from "sonner";
import { track } from "@crm/analytics";
import {
    useTriggerService,
    SKILL_TRIGGER_MAPPINGS,
} from "./components/TriggerService";
import WhatsAppTriggerModal from "./components/WhatsAppTriggerModal";
import ScheduleTriggerModal from "./components/ScheduleTriggerModal";
import { useWorkflow } from "../context/WorkflowContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Create the Chat Context
const ChatContext = createContext();

// Custom hook to use the Chat Context
export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChatContext must be used within a ChatProvider");
    }
    return context;
};

// Chat Provider component
export const ChatProvider = ({ children, confirm }) => {
    const navigate = useNavigate();
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);

    const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
    const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
    const [pendingWhatsappSkill, setPendingWhatsappSkill] = useState(null);
    const [selectedWhatsappNumber, setSelectedWhatsappNumber] = useState(null);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [isWaitingForTriggerData, setIsWaitingForTriggerData] =
        useState(false);
    const [pendingScheduleSkill, setPendingScheduleSkill] = useState(null);

    // Edit WhatsApp trigger states
    const [editingTrigger, setEditingTrigger] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [pendingMessage, setPendingMessage] = useState(null);
    const [prefillMessage, setPrefillMessage] = useState("");
    // Monotonic counter bumped by consumers (e.g. RollbackButton) when they
    // need the app-preview iframe to remount even though stepId / version /
    // environment haven't changed. AppPreviewDashboard subscribes via an
    // effect and calls its local reloadIframe() on each bump.
    const [previewReloadKey, setPreviewReloadKey] = useState(0);
    const requestPreviewReload = useCallback(() => {
        setPreviewReloadKey((k) => k + 1);
    }, []);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [stagedFiles, setStagedFiles] = useState([]);
    // Local staged files - files selected but not yet uploaded (no workflow needed)
    const [localStagedFiles, setLocalStagedFiles] = useState([]);
    const timerCheckTriggerData = React.useRef(null);
    const fetchRefApiKey = React.useRef(null);
    // Ref to track workflow creation promise (avoids race conditions)
    const workflowCreationPromiseRef = React.useRef(null);

    // App Preview View Mode (Preview vs Dashboard)

    const [selectedPageId, setSelectedPageId] = useState(null);
    const [iframePageId, setIframePageId] = useState(null); // Page reported by iframe navigation (display-only)

    // Markdown preview state
    const [markdownPreview, setMarkdownPreview] = useState(null); // { title, content }

    // View As User state (admin impersonation for app preview)
    // Shape: { id, username, token } or null
    const [viewAsUser, setViewAsUser] = useState(null);

    // Component reference state (for referencing components in chat)
    // Shape: { componentId, componentName, filePath, elementSelector?, elementTag? }
    const [componentRef, setComponentRef] = useState(null);
    const [isSelectMode, setIsSelectMode] = useState(false);

    const clearComponentRef = useCallback(() => {
        setComponentRef(null);
    }, []);

    const toggleSelectMode = useCallback(() => {
        setIsSelectMode((prev) => !prev);
    }, []);

    const openMarkdownPreview = useCallback((title, content) => {
        setMarkdownPreview({ title, content });
        // On mobile, switch to preview tab to show the markdown panel
        setAppViewMode("preview");
    }, []);

    const closeMarkdownPreview = useCallback(() => {
        setMarkdownPreview(null);
    }, []);

    // Dashboard navigation state
    const [appViewMode, setAppViewMode] = useState(() => {
        if (window?.innerWidth < 768) {
            return "chat";
        }
        return "preview";
    });
    const [previewDevice, setPreviewDevice] = useState(() => {
        if (window?.innerWidth < 768) {
            return "mobile";
        }
        return "desktop";
    });
    const [selectedRightPanel, setSelectedRightPanel] = useState("overview");
    // const [mobileView, setMobileView] = useState("preview"); // "chat" or "preview"

    // Environment state for workflow environments (development, staging, production)
    const [selectedEnvironment, setSelectedEnvironment] = useState();
    const [components, setComponents] = useState([]);

    // Get workflow from WorkflowContext
    const { workflow, updateWorkflow, handleFileUpload, refetchWorkflow } =
        useWorkflow();

    const isCurrentEnvironmentLocked = React.useMemo(() => {
        return (
            workflow?.isProductionLocked &&
            (selectedEnvironment === "production" ||
                selectedEnvironment == null)
        );
    }, [workflow?.isProductionLocked, selectedEnvironment]);

    // File upload API call
    const {
        call: insertFile,
        error: uploadError,
        isLoading: isUploading,
    } = useBluelagoon({
        method: "insertFile",
    });

    // API calls
    const {
        call: fetchEnvironmentsApi,
        response: environmentsResponse,
        error: environmentsError,
        isLoading: isLoadingEnvironments,
    } = useBluelagoon({
        method: "getWorkflowEnvironments",
        resetResponse: false,
    });

    // Ref to ensure environments are only fetched once per workflow
    const fetchEnvironmentsRef = React.useRef(null);
    const fetchComponentsTimeout = React.useRef(null);

    // Fetch environments function
    const fetchEnvironments = useCallback(() => {
        if (!workflow?.id) return;

        fetchEnvironmentsApi({ workflowId: workflow.id });
    }, [workflow?.id, fetchEnvironmentsApi]);

    // Fetch environments when workflow is available
    React.useEffect(() => {
        if (workflow?.id && fetchEnvironmentsRef.current !== workflow.id) {
            fetchEnvironments();
            fetchEnvironmentsRef.current = workflow.id;
        }
    }, [workflow?.id, fetchEnvironments]);

    // Initialize selectedEnvironment from workflow's lastUsedEnvironment
    const initEnvRef = React.useRef(null);
    React.useEffect(() => {
        if (workflow?.id && initEnvRef.current !== workflow.id) {
            if (workflow.lastUsedEnvironment !== undefined) {
                setSelectedEnvironment(workflow.lastUsedEnvironment);
            }
            initEnvRef.current = workflow.id;
        }
    }, [workflow?.id, workflow?.lastUsedEnvironment]);

    // Extract environments from response
    const environments = React.useMemo(() => {
        return environmentsResponse?.environments || [];
    }, [environmentsResponse?.environments]);

    // Extract production version ID from response
    const productionVersionId =
        environmentsResponse?.productionVersionId || null;

    // Delete file API call
    const { call: removeFileFromWorkflow, isLoading: isDeleting } =
        useBluelagoon({
            method: "removeFileFromWorkflow",
        });

    // Transfer to public API call
    const { call: transferToPublic, isLoading: isTransferring } = useBluelagoon(
        {
            method: "transferFileToPublic",
        }
    );

    // Workflow creation API call
    const { call: createWorkflow, isLoading: isWorkflowLoading } =
        useBluelagoon({
            method: "createWorkflow",
        });

    // WhatsApp numbers API call
    const { call: fetchWhatsAppNumbers } = useBluelagoon({
        method: "getAvailableWhatsappNumbers",
    });

    // Check trigger data API call
    const { call: checkTriggerData } = useBluelagoon({
        method: "checkTriggerData",
    });

    // Trigger service for handling trigger generation
    const { generateTriggerForSkill, SKILL_TRIGGER_MAPPINGS, deleteTrigger } =
        useTriggerService();

    const handleDeleteTrigger = useCallback(
        async (triggerId, workflowId) => {
            await confirm({
                title: "Delete Trigger",
                description: "Are you sure you want to delete this trigger?",
            });
            await deleteTrigger(triggerId, workflowId);
            await refetchWorkflow();
        },
        [deleteTrigger, refetchWorkflow]
    );

    // Update trigger function
    const { call: updateTrigger } = useBluelagoon({
        method: "updateWorkflowTrigger",
    });

    const handleUpdateTrigger = useCallback(
        async (triggerId, triggerData) => {
            try {
                const result = await updateTrigger({
                    workflowTriggerId: triggerId,
                    ...triggerData,
                });

                if (result?.status === 200) {
                    await refetchWorkflow();
                    toast.success("Trigger updated successfully");
                    return result;
                } else {
                    throw new Error(
                        result?.message || "Failed to update trigger"
                    );
                }
            } catch (error) {
                console.error("Error updating trigger:", error);
                toast.error("Failed to update trigger");
                throw error;
            }
        },
        [updateTrigger, refetchWorkflow]
    );

    // Fetch components for APP_V2
    const stepId = workflow?.steps?.[0]?.id;
    const {
        call: fetchComponentsApi,

        error: componentsError,
        isLoading: componentsLoading,
    } = useBluelagoon({
        method: "getComponentsByStepId",
        resetResponse: false,
    });

    const fetchComponents = useCallback(async () => {
        if (stepId) {
            if (fetchComponentsTimeout.current) {
                clearTimeout(fetchComponentsTimeout.current);
                fetchComponentsTimeout.current = null;
            }
            fetchComponentsTimeout.current = setTimeout(async () => {
                const result = await fetchComponentsApi({
                    stepId,
                    environmentName: selectedEnvironment,
                });

                if (result?.status === 200) {
                    setComponents(result?.data?.components || []);
                }
            }, 400);
        }
    }, [stepId, selectedEnvironment, fetchComponentsApi]);

    // Fetch components when stepId or version changes
    React.useEffect(() => {
        if (
            stepId &&
            fetchRefApiKey.current !==
                stepId +
                    workflow?.steps?.[0]?.currentVersion +
                    (selectedEnvironment ?? "production")
        ) {
            fetchComponents();
        }
    }, [
        fetchComponents,
        workflow?.steps?.[0]?.currentVersion,
        stepId,
        selectedEnvironment,
    ]);

    // Handle file upload
    const onFileUpload = useCallback(
        async (file) => {
            try {
                const result = await insertFile({
                    key: file.key,
                    name: file.file.name,
                    type: file.file.type,
                    size: file.file.size,
                    url: file.permanentUrl,
                    bucket: file.bucket,
                    region: file.region,
                    integrationId: file.integrationId,
                });

                // Add the new file to our uploaded files array
                if (result?.file?.id) {
                    handleFileUpload(result.file);
                    // Stage the file for attachment to next message
                    setStagedFiles((prev) => [...prev, result.file]);

                    return result.file;
                }
            } catch (error) {
                console.error("Failed to upload file:", error);
                throw error;
            }
        },
        [insertFile, handleFileUpload]
    );

    // Stage files for next message
    const stageFiles = useCallback((files) => {
        setStagedFiles((prev) => [...prev, ...files]);
    }, []);

    // Clear staged files after message send
    const clearStagedFiles = useCallback(() => {
        setStagedFiles([]);
    }, []);

    // Remove a staged file
    const removeStagedFile = useCallback((fileId) => {
        setStagedFiles((prev) => prev.filter((file) => file.id !== fileId));
    }, []);

    // Stage files locally (before upload, no workflow needed)
    const stageFilesLocally = useCallback((files) => {
        const filesWithMeta = Array.from(files).map((file) => ({
            id: makeId(8),
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            status: "local", // Not uploaded yet
            preview: file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : null,
        }));
        setLocalStagedFiles((prev) => [...prev, ...filesWithMeta]);
    }, []);

    // Remove a locally staged file
    const removeLocalStagedFile = useCallback((fileId) => {
        setLocalStagedFiles((prev) => {
            const file = prev.find((f) => f.id === fileId);
            // Revoke object URL to prevent memory leaks
            if (file?.preview) {
                URL.revokeObjectURL(file.preview);
            }
            return prev.filter((f) => f.id !== fileId);
        });
    }, []);

    // Clear all locally staged files
    const clearLocalStagedFiles = useCallback(() => {
        setLocalStagedFiles((prev) => {
            // Revoke all object URLs
            prev.forEach((file) => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
            return [];
        });
    }, []);

    // Handle making file public
    const handleMakePublic = useCallback(
        async (fileId) => {
            try {
                const result = await transferToPublic({
                    fileId,
                });

                if (result?.status === 200) {
                    toast.success("File made public");
                    // Refetch workflow to get updated file data
                    await refetchWorkflow();
                    return result;
                } else {
                    throw new Error(
                        result?.message || "Failed to make file public"
                    );
                }
            } catch (error) {
                console.error("Failed to make file public:", error);
                toast.error("Failed to make file public");
                throw error;
            }
        },
        [transferToPublic, refetchWorkflow]
    );

    // Select integration for chat
    const selectIntegration = useCallback((integration) => {
        setSelectedIntegration(integration);
        toast.success(`Using ${integration.name} integration`);
    }, []);

    // Clear selected integration
    const clearSelectedIntegration = useCallback(() => {
        setSelectedIntegration(null);
        toast.success("Integration cleared");
    }, []);

    // Ensure workflow exists before performing actions
    const ensureWorkflowExists = useCallback(async () => {
        if (workflow) {
            return workflow;
        }

        // If workflow creation is already in progress, wait for that promise
        if (workflowCreationPromiseRef.current) {
            return workflowCreationPromiseRef.current;
        }

        // Create the workflow and store the promise
        const createWorkflowPromise = (async () => {
            setIsCreatingWorkflow(true);
            try {
                const workflowName = `Chat Session - ${new Date().toLocaleString()}`;
                const result = await createWorkflow({
                    name: workflowName,
                });

                track("New Automation", {
                    name: workflowName,
                    type: "AUTOMATION",
                });

                if (result?.workflowId) {
                    const newWorkflow = {
                        id: result.workflowId,
                        name: workflowName,
                        type: "AUTOMATION",
                        newWorkflow: true,
                    };

                    // Refetch the workflow to get the complete workflow data including triggers
                    await refetchWorkflow();

                    return newWorkflow;
                } else {
                    throw new Error("Failed to create workflow");
                }
            } catch (error) {
                console.error("Error creating workflow:", error);
                toast.error("Failed to create chat workflow");
                throw error;
            } finally {
                setIsCreatingWorkflow(false);
                workflowCreationPromiseRef.current = null;
            }
        })();

        workflowCreationPromiseRef.current = createWorkflowPromise;
        return createWorkflowPromise;
    }, [workflow, createWorkflow, refetchWorkflow]);

    // Handle WhatsApp trigger modal confirmation
    const handleWhatsAppTriggerConfirm = useCallback(
        async (allowedPhones) => {
            if (isEditMode && editingTrigger) {
                // Edit existing trigger

                await handleUpdateTrigger(editingTrigger.id, {
                    type: "WHATSAPP",
                    workflowId: workflow?.id,
                    whatsappNumberId:
                        editingTrigger.whatsappTrigger?.whatsappNumber?.id,
                    config: {
                        allowedPhones: allowedPhones.map((phone) =>
                            phone.replace(/[\s\-+]/g, "")
                        ),
                    },
                });

                // Reset edit state
                setEditingTrigger(null);
                setIsEditMode(false);
                setWhatsappModalOpen(false);
            } else {
                // Create new trigger (existing logic)
                if (!pendingWhatsappSkill || !selectedWhatsappNumber) return;

                toast.info(
                    `Creating workflow and generating WhatsApp trigger for ${pendingWhatsappSkill.name}...`
                );

                try {
                    // Ensure workflow exists before generating trigger
                    const workflow = await ensureWorkflowExists();

                    const triggerResult = await generateTriggerForSkill(
                        pendingWhatsappSkill,
                        {
                            workflowId: workflow.id,
                            selectedNumberId: selectedWhatsappNumber.id,
                            allowedPhones: allowedPhones,
                        }
                    );

                    if (workflow.newWorkflow) {
                        navigate(`/chat/${workflow.id}`);
                    } else {
                        setTimeout(() => {
                            refetchWorkflow();
                        }, 200);
                    }

                    if (triggerResult.success) {
                        // Trigger will be added to workflow.workflowTriggers via the API
                        // Refetch workflow to get updated triggers

                        // Reset modal state
                        setPendingWhatsappSkill(null);
                        setSelectedWhatsappNumber(null);
                        setWhatsappModalOpen(false);
                    } else {
                        throw Error(triggerResult.error);
                    }
                } catch (error) {
                    throw Error(error);
                }
            }
        },
        [
            isEditMode,
            editingTrigger,
            handleUpdateTrigger,
            pendingWhatsappSkill,
            selectedWhatsappNumber,
            ensureWorkflowExists,
            generateTriggerForSkill,
            refetchWorkflow,
            workflow?.id,
            navigate,
        ]
    );

    // Handle schedule trigger modal confirmation
    const handleScheduleTriggerConfirm = useCallback(
        async (scheduleConfig) => {
            if (!pendingScheduleSkill) return;

            toast.info(
                `Creating workflow and generating schedule trigger for ${pendingScheduleSkill.name}...`
            );

            try {
                // Ensure workflow exists before generating trigger
                const workflow = await ensureWorkflowExists();

                if (workflow.newWorkflow) {
                    navigate(`/chat/${workflow.id}`);
                }

                const triggerResult = await generateTriggerForSkill(
                    pendingScheduleSkill,
                    {
                        workflowId: workflow.id,
                        timeOfDay: scheduleConfig.timeOfDay,
                        timezone: scheduleConfig.timezone,
                        cron: scheduleConfig.cron,
                        weekdays: scheduleConfig.weekdays || [],
                        daysOfMonth: scheduleConfig.daysOfMonth || [],
                    }
                );

                if (triggerResult.success) {
                    // Trigger will be added to workflow.workflowTriggers via the API
                    // Refetch workflow to get updated triggers
                    setTimeout(() => {
                        refetchWorkflow();
                    }, 500);
                } else {
                    toast.warning(
                        `${pendingScheduleSkill.name} skill added, but trigger generation failed: ${triggerResult.error}`
                    );
                }
            } catch (error) {
                console.error("Error generating schedule trigger:", error);
                toast.warning(
                    `${pendingScheduleSkill.name} skill added, but trigger generation failed`
                );
            } finally {
                // Reset modal state
                setPendingScheduleSkill(null);
                setScheduleModalOpen(false);
            }
        },
        [
            pendingScheduleSkill,
            ensureWorkflowExists,
            generateTriggerForSkill,
            refetchWorkflow,
            navigate,
        ]
    );

    // Handle WhatsApp trigger modal cancellation
    const handleWhatsAppTriggerCancel = useCallback(() => {
        if (isEditMode) {
            // Reset edit state
            setEditingTrigger(null);
            setIsEditMode(false);
            setWhatsappModalOpen(false);
        } else {
            // Remove the skill that was added but not configured
            if (pendingWhatsappSkill) {
                setSelectedSkills((prev) =>
                    prev.filter((s) => s.id !== pendingWhatsappSkill.id)
                );
                toast.info(`${pendingWhatsappSkill.name} skill cancelled`);
            }

            // Reset modal state
            setPendingWhatsappSkill(null);
            setSelectedWhatsappNumber(null);
            setWhatsappModalOpen(false);
        }
    }, [pendingWhatsappSkill, isEditMode]);
    // Handle WhatsApp trigger modal cancellation
    const handleWhatsAppTriggerClose = useCallback(() => {
        setWhatsappModalOpen(false);
    }, []);

    // Handle editing WhatsApp trigger
    const handleEditWhatsAppTrigger = useCallback((trigger) => {
        setEditingTrigger(trigger);
        setIsEditMode(true);
        setWhatsappModalOpen(true);
    }, []);

    // Handle schedule trigger modal cancellation
    const handleScheduleTriggerCancel = useCallback(() => {
        // Remove the skill that was added but not configured
        if (pendingScheduleSkill) {
            setSelectedSkills((prev) =>
                prev.filter((s) => s.id !== pendingScheduleSkill.id)
            );
            toast.info(`${pendingScheduleSkill.name} skill cancelled`);
        }

        // Reset modal state
        setPendingScheduleSkill(null);
        setScheduleModalOpen(false);
    }, [pendingScheduleSkill]);

    // Add skill to selected skills
    const addSkill = useCallback(
        async (skill) => {
            // Check if skill is already selected
            const isAlreadySelected = selectedSkills.some(
                (s) => s.id === skill.id
            );
            if (isAlreadySelected) {
                toast.info(`${skill.name} is already selected`);
                return;
            }

            if (!skill.isTrigger) {
                setSelectedSkills((prev) => [...prev, skill]);
            }

            // If this is a trigger skill, handle trigger configuration
            if (skill.isTrigger && SKILL_TRIGGER_MAPPINGS[skill.name]) {
                // Special handling for WhatsApp triggers - need phone number selection
                if (SKILL_TRIGGER_MAPPINGS[skill.name].type === "WHATSAPP") {
                    toast.info("Fetching available WhatsApp numbers...");

                    try {
                        const numbersResponse = await fetchWhatsAppNumbers({});
                        const availableNumbers = (
                            numbersResponse?.data || {}
                        )?.numbers?.filter(
                            (number) => number.isAvailableForCompany
                        );

                        if (
                            !availableNumbers ||
                            availableNumbers.length === 0
                        ) {
                            toast.error(
                                "No WhatsApp numbers are available. Please add a WhatsApp integration first."
                            );
                            return;
                        }

                        // Store the skill and number for modal
                        setPendingWhatsappSkill(skill);
                        setSelectedWhatsappNumber(availableNumbers[0]);
                        setWhatsappModalOpen(true);
                    } catch (error) {
                        console.error(
                            "Error fetching WhatsApp numbers:",
                            error
                        );
                        toast.error("Failed to fetch WhatsApp numbers");
                    }
                } else if (SKILL_TRIGGER_MAPPINGS[skill.name].type === "TIME") {
                    // Special handling for Schedule triggers - need schedule configuration
                    toast.info("Opening schedule configuration...");

                    // Store the skill for modal
                    setPendingScheduleSkill(skill);
                    setScheduleModalOpen(true);
                } else {
                    // Handle other trigger types (Email, Webhook) immediately
                    toast.info(
                        `Creating workflow and generating trigger for ${skill.name}...`
                    );

                    try {
                        // Ensure workflow exists before generating trigger
                        const workflow = await ensureWorkflowExists();

                        const triggerResult = await generateTriggerForSkill(
                            skill,
                            {
                                workflowId: workflow.id,
                            }
                        );

                        if (workflow.newWorkflow) {
                            navigate(`/chat/${workflow.id}`);
                        }

                        if (triggerResult.success) {
                            // Trigger will be added to workflow.workflowTriggers via the API
                            // Refetch workflow to get updated triggers
                            await refetchWorkflow();
                        } else {
                            // Still add the skill even if trigger generation fails
                            toast.warning(
                                `${skill.name} skill added, but trigger generation failed: ${triggerResult.error}`
                            );
                        }
                    } catch (error) {
                        console.error(
                            "Error generating trigger for skill:",
                            error
                        );
                        toast.warning(
                            `${skill.name} skill added, but trigger generation failed`
                        );
                    }
                }
            } else {
                toast.success(`${skill.name} skill added`);
            }
        },
        [
            selectedSkills,
            generateTriggerForSkill,
            SKILL_TRIGGER_MAPPINGS,
            ensureWorkflowExists,
            refetchWorkflow,
        ]
    );

    // Remove skill from selected skills
    const removeSkill = useCallback(
        async (skillId) => {
            const skill = selectedSkills.find((s) => s.id === skillId);
            if (skill) {
                // Triggers will be removed from workflow.workflowTriggers via the API
                // Refetch workflow to get updated triggers
                await refetchWorkflow();
                toast.success(`${skill.name} skill removed`);
            }
            setSelectedSkills((prev) => prev.filter((s) => s.id !== skillId));
        },
        [selectedSkills, refetchWorkflow]
    );

    // Clear all selected skills
    const clearAllSkills = useCallback(() => {
        setSelectedSkills([]);
        toast.success("All skills cleared");
    }, []);

    // Use the uploader hook
    const {
        uploadFiles,
        files: uploaderFiles,
        loading: uploaderLoading,
        removeFile,
    } = useUploader({
        onFileUpload,
        integrationId: null, // Can be set based on selected integration
    });

    // Upload all locally staged files and move them to staged files
    const uploadLocalStagedFiles = useCallback(async () => {
        if (localStagedFiles.length === 0) return [];

        const filesToUpload = localStagedFiles.map((f) => f.file);

        // Clear local staged files (they're being uploaded now)
        clearLocalStagedFiles();

        // Upload the files - this will add them to stagedFiles via onFileUpload
        const results = await uploadFiles(filesToUpload);

        // Extract the uploaded file objects from the results
        const uploadedFiles =
            results?.filter((r) => r?.success && r?.file)?.map((r) => r.file) ||
            [];

        return uploadedFiles;
    }, [localStagedFiles, uploadFiles, clearLocalStagedFiles]);

    const handleDeleteFile = useCallback(
        async ({ fileId }) => {
            await removeFileFromWorkflow({
                fileIds: [fileId],
                workflowId: workflow?.id,
            });
            refetchWorkflow();
        },
        [removeFileFromWorkflow, refetchWorkflow, workflow?.id]
    );

    // Check for trigger data every 2 seconds
    const checkForTriggerData = useCallback(
        async (forceRefresh = false) => {
            if (!forceRefresh) {
                if (
                    !workflow?.workflowTriggers ||
                    workflow.workflowTriggers.length === 0 ||
                    !workflow?.id ||
                    workflow?.testData
                )
                    return;
            }

            try {
                const response = await checkTriggerData({
                    id: workflow.id, // Updated to match new API parameter
                });

                if (response?.data?.hasAnyData) {
                    refetchWorkflow();
                    if (timerCheckTriggerData.current) {
                        clearInterval(timerCheckTriggerData.current);
                        timerCheckTriggerData.current = null;
                        setIsWaitingForTriggerData(false);
                    }
                }
            } catch (error) {
                console.error("Error checking trigger data:", error);
            }
        },
        [workflow?.workflowTriggers, workflow?.id, checkTriggerData]
    );

    // Dashboard navigation handlers
    const navigateToOverview = useCallback(() => {
        setSelectedRightPanel("overview");
        setAppViewMode("dashboard");
    }, []);

    const navigateToUsers = useCallback(() => {
        setSelectedRightPanel("users");
        setAppViewMode("dashboard");
    }, []);

    const navigateToDomains = useCallback(() => {
        setSelectedRightPanel("domains");
        setAppViewMode("dashboard");
    }, []);

    const navigateToAuthentication = useCallback(() => {
        setSelectedRightPanel("authentication");
        setAppViewMode("dashboard");
    }, []);

    const navigateToDataSecurity = useCallback(() => {
        setSelectedRightPanel("data-security");
        setAppViewMode("dashboard");
    }, []);

    const navigateToDatabaseSharing = useCallback(() => {
        setSelectedRightPanel("database-sharing");
        setAppViewMode("dashboard");
    }, []);

    const navigateToFunctionSharing = useCallback(() => {
        setSelectedRightPanel("function-sharing");
        setAppViewMode("dashboard");
    }, []);

    const navigateToIntegrations = useCallback(() => {
        setSelectedRightPanel("integrations");
        setAppViewMode("dashboard");
    }, []);

    const navigateToSecrets = useCallback(() => {
        setSelectedRightPanel("secrets");
        setAppViewMode("dashboard");
    }, []);

    const navigateToSettings = useCallback(() => {
        setSelectedRightPanel("settings");
        setAppViewMode("dashboard");
    }, []);

    const navigateToAPIs = useCallback(() => {
        setSelectedRightPanel("apis");
        setAppViewMode("dashboard");
    }, []);

    const navigateToEnvironments = useCallback(() => {
        setSelectedRightPanel("environments");
        setAppViewMode("dashboard");
    }, []);

    const navigateToEditor = useCallback(() => {
        setSelectedRightPanel(null);
        setAppViewMode("dashboard");
    }, []);

    const navigateToVersionHistory = useCallback(() => {
        setSelectedRightPanel("version-history");
        setAppViewMode("dashboard");
    }, []);

    const navigateToImport = useCallback(() => {
        setSelectedRightPanel("import");
        setAppViewMode("dashboard");
    }, []);

    const navigateToCodeScanning = useCallback(() => {
        setSelectedRightPanel("code-scanning");
        setAppViewMode("dashboard");
    }, []);

    const navigateToTesting = useCallback(() => {
        setSelectedRightPanel("testing");
        setAppViewMode("dashboard");
    }, []);

    const navigateToActivity = useCallback(() => {
        setSelectedRightPanel("activity");
        setAppViewMode("dashboard");
    }, []);

    // Start polling when triggers exist
    React.useEffect(() => {
        if (
            workflow?.workflowTriggers &&
            workflow.workflowTriggers.length > 0 &&
            workflow.workflowTriggers.some(
                (trigger) => trigger.type !== "TIME" && trigger.type !== "APP"
            ) &&
            workflow?.id
        ) {
            setIsWaitingForTriggerData(true);
            // Check immediately
            checkForTriggerData();

            // Set up polling every 2 seconds
            timerCheckTriggerData.current = setInterval(() => {
                checkForTriggerData();
            }, 2000);
        } else {
            // Clear polling when no triggers
            if (timerCheckTriggerData.current) {
                clearInterval(timerCheckTriggerData.current);
                timerCheckTriggerData.current = null;
            }
            setIsWaitingForTriggerData(false);
        }

        // Cleanup on unmount
        return () => {
            if (timerCheckTriggerData.current) {
                clearInterval(timerCheckTriggerData.current);
                timerCheckTriggerData.current = null;
                setIsWaitingForTriggerData(false);
            }
        };
    }, [workflow?.workflowTriggers?.length, workflow?.id, checkForTriggerData]);

    // Handler to update environment and persist to backend
    const handleEnvironmentChange = useCallback(
        (environmentName) => {
            setSelectedEnvironment(environmentName);
            if (workflow?.id) {
                updateWorkflow({ lastUsedEnvironment: environmentName }).catch(
                    (error) => {
                        console.error(
                            "Error persisting lastUsedEnvironment:",
                            error
                        );
                    }
                );
            }
        },
        [workflow?.id, updateWorkflow]
    );

    const value = React.useMemo(
        () => ({
            // File states
            files: uploaderFiles,
            removeFile,
            // Upload functions
            uploadFiles,
            onFileUpload,
            handleMakePublic,

            // Staged files for messages
            stagedFiles,
            stageFiles,
            clearStagedFiles,
            removeStagedFile,

            // Local staged files (not uploaded yet)
            localStagedFiles,
            stageFilesLocally,
            removeLocalStagedFile,
            clearLocalStagedFiles,
            uploadLocalStagedFiles,

            // Integration states
            selectedIntegration,
            setSelectedIntegration,
            selectIntegration,
            clearSelectedIntegration,

            // Skills states
            selectedSkills,
            setSelectedSkills,
            addSkill,
            removeSkill,
            clearAllSkills,

            checkForTriggerData,

            // Workflow states (from WorkflowContext)
            workflow,
            updateWorkflow,
            isWorkflowLoading,
            ensureWorkflowExists,

            // WhatsApp modal states
            whatsappModalOpen,
            setWhatsappModalOpen,
            pendingWhatsappSkill,
            selectedWhatsappNumber,
            handleWhatsAppTriggerConfirm,
            handleWhatsAppTriggerCancel,
            handleEditWhatsAppTrigger,
            editingTrigger,
            isEditMode,

            // Schedule modal states
            scheduleModalOpen,
            setScheduleModalOpen,
            pendingScheduleSkill,
            handleScheduleTriggerConfirm,
            handleScheduleTriggerCancel,

            isTransferring,
            isLoadingFileUpload: uploaderLoading || isUploading,

            // Errors
            uploadError,

            // Pending message
            pendingMessage,
            setPendingMessage,
            prefillMessage,
            setPrefillMessage,

            // App preview reload coordination
            previewReloadKey,
            requestPreviewReload,

            // Confirm
            confirm,

            // Delete file
            handleDeleteFile,
            isDeleting,
            refetchWorkflow,
            isWaitingForTriggerData,

            handleDeleteTrigger,
            pendingFiles,
            setPendingFiles,

            // Components (for APP_V2)
            components,
            fetchComponents,
            isComponentsLoading: componentsLoading,
            componentsError,

            // App Preview
            appViewMode,
            setAppViewMode,
            previewDevice,
            setPreviewDevice,
            selectedPageId,
            setSelectedPageId,
            iframePageId,
            setIframePageId,

            // Environment state
            selectedEnvironment,
            setSelectedEnvironment: handleEnvironmentChange,

            // Dashboard navigation
            selectedRightPanel,
            setSelectedRightPanel,
            navigateToOverview,
            navigateToUsers,
            navigateToDomains,
            navigateToAuthentication,
            navigateToDataSecurity,
            navigateToDatabaseSharing,
            navigateToFunctionSharing,
            navigateToIntegrations,
            navigateToSecrets,
            navigateToSettings,
            navigateToAPIs,
            navigateToEnvironments,
            navigateToEditor,
            navigateToVersionHistory,
            navigateToImport,
            navigateToCodeScanning,
            navigateToTesting,
            navigateToActivity,
            isCurrentEnvironmentLocked,

            // Environments
            environments,
            fetchEnvironments,
            isLoadingEnvironments,
            productionVersionId,

            // Markdown preview
            markdownPreview,
            openMarkdownPreview,
            closeMarkdownPreview,

            // View As User (admin impersonation)
            viewAsUser,
            setViewAsUser,

            // Component reference (for visual editor / component referencing)
            componentRef,
            setComponentRef,
            clearComponentRef,
            isSelectMode,
            setIsSelectMode,
            toggleSelectMode,
        }),
        [
            // File states
            uploaderFiles,
            removeFile,
            // Upload functions
            uploadFiles,
            onFileUpload,
            handleMakePublic,

            // Staged files for messages
            stagedFiles,
            stageFiles,
            clearStagedFiles,
            removeStagedFile,

            // Local staged files
            localStagedFiles,
            stageFilesLocally,
            removeLocalStagedFile,
            clearLocalStagedFiles,
            uploadLocalStagedFiles,

            // Integration states
            selectedIntegration,
            setSelectedIntegration,
            selectIntegration,
            clearSelectedIntegration,

            // Skills states
            selectedSkills,
            setSelectedSkills,
            addSkill,
            removeSkill,
            clearAllSkills,

            checkForTriggerData,

            // Workflow states (from WorkflowContext)
            workflow,
            updateWorkflow,
            isWorkflowLoading,
            ensureWorkflowExists,

            // WhatsApp modal states
            whatsappModalOpen,
            setWhatsappModalOpen,
            pendingWhatsappSkill,
            selectedWhatsappNumber,
            handleWhatsAppTriggerConfirm,
            handleWhatsAppTriggerCancel,
            handleEditWhatsAppTrigger,
            editingTrigger,
            isEditMode,

            // Schedule modal states
            scheduleModalOpen,
            setScheduleModalOpen,
            pendingScheduleSkill,
            handleScheduleTriggerConfirm,
            handleScheduleTriggerCancel,

            isTransferring,
            uploaderLoading,

            // Errors
            uploadError,

            // Pending message
            pendingMessage,
            setPendingMessage,
            prefillMessage,
            setPrefillMessage,

            // App preview reload coordination
            previewReloadKey,
            requestPreviewReload,

            // Confirm
            confirm,

            // Delete file
            handleDeleteFile,
            isDeleting,
            refetchWorkflow,
            isWaitingForTriggerData,

            handleDeleteTrigger,
            pendingFiles,
            setPendingFiles,

            // Components (for APP_V2)
            components,
            componentsLoading,
            componentsError,

            // App Preview
            appViewMode,
            setAppViewMode,
            previewDevice,
            setPreviewDevice,
            selectedPageId,
            setSelectedPageId,
            iframePageId,
            setIframePageId,

            // Environment state
            selectedEnvironment,
            handleEnvironmentChange,

            // Dashboard navigation
            selectedRightPanel,
            setSelectedRightPanel,
            navigateToOverview,
            navigateToUsers,
            navigateToDomains,
            navigateToAuthentication,
            navigateToDataSecurity,
            navigateToDatabaseSharing,
            navigateToFunctionSharing,
            navigateToIntegrations,
            navigateToSecrets,
            navigateToSettings,
            navigateToAPIs,
            navigateToEnvironments,
            navigateToEditor,
            navigateToVersionHistory,
            navigateToCodeScanning,
            navigateToTesting,
            navigateToActivity,
            isCurrentEnvironmentLocked,

            // Environments
            environments,
            fetchEnvironments,
            isLoadingEnvironments,
            productionVersionId,

            // Markdown preview
            markdownPreview,
            openMarkdownPreview,
            closeMarkdownPreview,

            // View As User
            viewAsUser,
            setViewAsUser,

            // Component reference
            componentRef,
            setComponentRef,
            clearComponentRef,
            isSelectMode,
            setIsSelectMode,
            toggleSelectMode,
        ]
    );

    return (
        <ChatContext.Provider value={value}>
            {children}

            {/* WhatsApp Trigger Configuration Modal */}
            <WhatsAppTriggerModal
                isOpen={whatsappModalOpen}
                handleCancel={handleWhatsAppTriggerCancel}
                onClose={handleWhatsAppTriggerClose}
                onConfirm={handleWhatsAppTriggerConfirm}
                selectedNumber={
                    isEditMode
                        ? editingTrigger?.whatsappTrigger?.whatsappNumber
                        : selectedWhatsappNumber
                }
                existingTrigger={editingTrigger}
                isEditMode={isEditMode}
            />

            {/* Schedule Trigger Configuration Modal */}
            <ScheduleTriggerModal
                isOpen={scheduleModalOpen}
                onClose={handleScheduleTriggerCancel}
                onConfirm={handleScheduleTriggerConfirm}
            />
        </ChatContext.Provider>
    );
};
